import { useState } from "react";
import { LuDownload, LuEllipsisVertical, LuShare, LuSquarePlus } from "react-icons/lu";
import { promptInstall, useInstall, type InstallHint } from "../install";
import { Modal } from "./ui";

/** how to install, in words a child (or a parent) can follow, for the browser at hand */
function HowTo({ hint }: { hint: InstallHint }) {
  switch (hint) {
    case "ios":
      return (
        <ol className="inst-steps">
          <li>Tryk på <strong>Del-knappen</strong> <LuShare aria-label="Del" size={18} /> nederst (eller øverst) i Safari.</li>
          <li>Rul ned og tryk på <strong>«Føj til hjemmeskærm»</strong> <LuSquarePlus aria-hidden="true" size={18} />.</li>
          <li>Tryk <strong>Tilføj</strong>. Nu ligger EduBattle som en app på din skærm.</li>
        </ol>
      );
    case "android":
      return (
        <ol className="inst-steps">
          <li>Tryk på <strong>menuen</strong> <LuEllipsisVertical aria-label="Menu" size={18} /> øverst i Chrome.</li>
          <li>Vælg <strong>«Installer app»</strong> eller <strong>«Føj til startskærm»</strong>.</li>
          <li>Tryk <strong>Installer</strong>.</li>
        </ol>
      );
    case "chromium":
      return (
        <ol className="inst-steps">
          <li>Kig i adresselinjen øverst: der er et lille <strong>installér-ikon</strong> <LuDownload aria-hidden="true" size={18} /> til højre.</li>
          <li>Klik på det og vælg <strong>«Installer»</strong>. (Eller åbn menuen ⋮ og vælg «Gem og del» → «Installer side som app».)</li>
        </ol>
      );
    case "safari":
      return (
        <ol className="inst-steps">
          <li>Vælg <strong>Arkiv → Føj til Dock</strong> i menuen øverst på skærmen.</li>
          <li>Så ligger EduBattle i dit Dock som en rigtig app.</li>
        </ol>
      );
    case "firefox":
      return <p>Firefox kan ikke gøre hjemmesider til apps på en computer. Åbn EduBattle i <strong>Chrome</strong> eller <strong>Edge</strong>, og tryk på «Hent app» igen. På en telefon kan du bruge menuen og vælge «Installer».</p>;
    default:
      return <p>Åbn menuen i din browser og led efter «Installer app» eller «Føj til startskærm». Virker det ikke, så prøv Chrome eller Edge.</p>;
  }
}

/** The "Hent som app" button. Hidden once the site already runs as an installed app. variant: "pill" (menu bar), "card" (a big friendly banner). */
export function InstallButton({ variant = "pill", className = "" }: { variant?: "pill" | "card"; className?: string }) {
  const { standalone, canPrompt, hint } = useInstall();
  const [help, setHelp] = useState(false);
  if (standalone) return null;
  const click = async () => {
    if (canPrompt) {
      const r = await promptInstall();
      if (r === "unavailable") setHelp(true);
    } else setHelp(true);
  };
  return (
    <>
      {variant === "card" ? (
        <button type="button" className={`install-card ${className}`} onClick={click}>
          <span className="ic-ico" aria-hidden="true"><LuDownload size={28} /></span>
          <span className="ic-text"><b>Hent EduBattle som app</b><small>På din telefon, tablet eller computer – åbner med ét tryk</small></span>
          <span className="btn lime small" aria-hidden="true">Hent app</span>
        </button>
      ) : (
        <button type="button" className={`install-pill ${className}`} onClick={click} title="Hent EduBattle som app">
          <LuDownload aria-hidden="true" size={17} /> <span>Hent app</span>
        </button>
      )}
      {help && (
        <Modal title="Hent EduBattle som app" onClose={() => setHelp(false)}>
          <p>Så kan du åbne EduBattle direkte fra din startskærm eller dit skrivebord – ligesom en almindelig app.</p>
          <HowTo hint={hint} />
          <div className="row"><button className="btn" onClick={() => setHelp(false)}>Det er forstået</button></div>
        </Modal>
      )}
    </>
  );
}
